import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useSearchParams } from 'react-router-dom';
import { 
  MessageSquare, 
  Plus, 
  Edit, 
  Trash2, 
  Image as ImageIcon,
  X,
  Send,
  AlertCircle,
  CheckCircle,
  Clock,
  User,
  Tag
} from 'lucide-react';
import { ticketsService } from '../services/ticketsService';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from '../components/LoadingSpinner';
import toast from 'react-hot-toast';

const Tickets = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showForm, setShowForm] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [formData, setFormData] = useState({
    subject: '',
    description: '',
    priority: 'sredni',
    screenshots: []
  });
  const [commentText, setCommentText] = useState('');
  const [commentScreenshots, setCommentScreenshots] = useState([]);
  const [activeTab, setActiveTab] = useState('nowy'); // wszystkie, nowy, w_trakcie, rozwiazany, zamkniety
  const commentTextareaRef = useRef(null);
  const formRef = useRef(null);

  const apiUrl = process.env.REACT_APP_API_URL || 'https://farm.my-it.pl/api';
  const uploadsUrl = apiUrl.replace('/api', '');

  const isSuperAdmin = user?.role === 'super admin';
  const isAdmin = user?.role === 'admin' || user?.role === 'super admin';

  // Obsługa wklejania obrazów ze schowka w formularzu zgłoszenia
  useEffect(() => {
    const handlePaste = (e) => {
      // Obsługuj tylko gdy formularz jest otwarty
      if (!showForm) return;

      const items = e.clipboardData?.items;
      if (!items) return;

      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        
        // Sprawdź czy to obraz
        if (item.type.indexOf('image') !== -1) {
          e.preventDefault();
          
          const file = item.getAsFile();
          if (file) {
            // Utwórz plik z datą w nazwie
            const timestamp = Date.now();
            const fileName = `screenshot-${timestamp}.png`;
            const blob = file.slice(0, file.size, 'image/png');
            const newFile = new File([blob], fileName, { type: 'image/png' });
            
            // Dodaj do listy screenshotów
            setFormData(prev => ({
              ...prev,
              screenshots: [...prev.screenshots, newFile]
            }));
            
            toast.success('Screenshot został wklejony');
          }
        }
      }
    };

    // Dodaj nasłuchiwanie na całym dokumencie
    document.addEventListener('paste', handlePaste);
    
    return () => {
      document.removeEventListener('paste', handlePaste);
    };
  }, [showForm]);

  // Funkcja obsługi wklejania screenshotów w komentarzach
  const handleCommentPaste = (e) => {
    const clipboardData = e.clipboardData;
    if (!clipboardData) {
      // Brak clipboardData - pozwól na normalne wklejanie
      return;
    }

    const items = clipboardData.items;
    if (!items || items.length === 0) {
      // Brak elementów - pozwól na normalne wklejanie tekstu
      return;
    }

    // Sprawdź czy jest obraz w schowku
    let hasImage = false;
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      
      // Sprawdź czy to obraz
      if (item && item.type && item.type.indexOf('image') !== -1) {
        hasImage = true;
        // Znaleziono obraz - zablokuj domyślne wklejanie
        e.preventDefault();
        e.stopPropagation();
        
        const file = item.getAsFile();
        if (file && file.size > 0) {
          // Utwórz plik z datą w nazwie
          const timestamp = Date.now();
          const fileName = `screenshot-${timestamp}.png`;
          
          // Zachowaj oryginalny typ MIME
          const mimeType = file.type || 'image/png';
          const newFile = new File([file], fileName, { type: mimeType });
          
          // Dodaj do listy screenshotów komentarza
          setCommentScreenshots(prev => [...prev, newFile]);
          
          toast.success('Screenshot został wklejony do komentarza');
        }
        break; // Zatrzymaj po znalezieniu pierwszego obrazu
      }
    }
    
    // Jeśli nie znaleziono obrazu, pozwól na normalne wklejanie tekstu (domyślne zachowanie)
  };

  // Pobierz zgłoszenia
  const { data, isLoading, error, refetch } = useQuery(
    'tickets',
    () => ticketsService.getTickets(),
    { 
      refetchInterval: 30000, // Odświeżaj co 30 sekund
      refetchOnWindowFocus: true, // Odświeżaj gdy użytkownik wraca do okna
      refetchOnMount: true // Odświeżaj przy montowaniu
    }
  );

  const tickets = data?.tickets || [];

  // Filtruj zgłoszenia według aktywnej zakładki
  const filteredTickets = activeTab === 'wszystkie' 
    ? tickets 
    : tickets.filter(ticket => ticket.status === activeTab);

  // Automatycznie wybierz zgłoszenie z URL params
  useEffect(() => {
    const ticketId = searchParams.get('ticket');
    if (ticketId && tickets.length > 0) {
      const ticket = tickets.find(t => t._id === ticketId);
      if (ticket && (!selectedTicket || selectedTicket._id !== ticketId)) {
        setSelectedTicket(ticket);
        // Usuń parametr z URL po wybraniu
        searchParams.delete('ticket');
        setSearchParams(searchParams, { replace: true });
      }
    }
  }, [tickets, searchParams, setSearchParams, selectedTicket]);

  // Mutacje
  const createMutation = useMutation(ticketsService.createTicket, {
    onSuccess: async (data) => {
      toast.success('Zgłoszenie zostało utworzone');
      // Odśwież listę zgłoszeń natychmiast
      await refetch();
      // Automatycznie wybierz nowo utworzone zgłoszenie
      if (data?.ticket) {
        // Poczekaj na odświeżenie listy, potem wybierz zgłoszenie
        setTimeout(() => {
          setSelectedTicket(data.ticket);
        }, 100);
      }
      resetForm();
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Błąd podczas tworzenia zgłoszenia');
    }
  });

  const updateMutation = useMutation(
    ({ id, data }) => ticketsService.updateTicket(id, data),
    {
      onSuccess: (response) => {
        toast.success('Zgłoszenie zostało zaktualizowane');
        // Odśwież listę zgłoszeń
        refetch();
        // Zaktualizuj wybrane zgłoszenie bezpośrednio
        if (response?.ticket && selectedTicket?._id === response.ticket._id) {
          setSelectedTicket(response.ticket);
          // Nie przełączamy zakładki - użytkownik pozostaje w tym samym miejscu
        }
        queryClient.invalidateQueries('tickets');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Błąd podczas aktualizacji zgłoszenia');
      }
    }
  );

  const deleteMutation = useMutation(ticketsService.deleteTicket, {
    onSuccess: () => {
      toast.success('Zgłoszenie zostało usunięte');
      queryClient.invalidateQueries('tickets');
      setSelectedTicket(null);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Błąd podczas usuwania zgłoszenia');
    }
  });

  const addCommentMutation = useMutation(
    ({ id, text, screenshots }) => ticketsService.addComment(id, text, screenshots),
    {
      onSuccess: async (response) => {
        toast.success('Komentarz został dodany');
        // Odśwież listę zgłoszeń natychmiast
        await refetch();
        queryClient.invalidateQueries('tickets');
        queryClient.invalidateQueries('notifications'); // Odśwież powiadomienia natychmiast
        // Zaktualizuj wybrane zgłoszenie z nowym komentarzem z odpowiedzi backendu
        if (selectedTicket && response?.ticket) {
          setSelectedTicket(response.ticket);
        } else if (selectedTicket) {
          // Fallback: jeśli nie ma ticket w odpowiedzi, odśwież z listy
          const updatedTickets = await refetch();
          if (updatedTickets?.data?.tickets) {
            const updatedTicket = updatedTickets.data.tickets.find(t => t._id === selectedTicket._id);
            if (updatedTicket) {
              setSelectedTicket(updatedTicket);
            }
          }
        }
        setCommentText('');
        setCommentScreenshots([]);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Błąd podczas dodawania komentarza');
      }
    }
  );

  const resetForm = () => {
    setFormData({
      subject: '',
      description: '',
      priority: 'sredni',
      screenshots: []
    });
    setShowForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.subject.trim() || !formData.description.trim()) {
      toast.error('Temat i opis są wymagane');
      return;
    }

    createMutation.mutate(formData);
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setFormData(prev => ({
      ...prev,
      screenshots: [...prev.screenshots, ...files]
    }));
  };

  const removeFile = (index) => {
    setFormData(prev => ({
      ...prev,
      screenshots: prev.screenshots.filter((_, i) => i !== index)
    }));
  };

  const handleDelete = (id) => {
    if (window.confirm('Czy na pewno chcesz usunąć to zgłoszenie?')) {
      deleteMutation.mutate(id);
    }
  };

  const handleStatusChange = (ticketId, newStatus) => {
    updateMutation.mutate({
      id: ticketId,
      data: { status: newStatus }
    });
  };

  const handleAddComment = (e) => {
    e.preventDefault();
    if ((!commentText.trim() && commentScreenshots.length === 0) || !selectedTicket) return;
    
    addCommentMutation.mutate({
      id: selectedTicket._id,
      text: commentText || '',
      screenshots: commentScreenshots
    });
  };

  const removeCommentScreenshot = (index) => {
    setCommentScreenshots(prev => prev.filter((_, i) => i !== index));
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'nowy':
        return 'bg-blue-100 text-blue-800';
      case 'w_trakcie':
        return 'bg-yellow-100 text-yellow-800';
      case 'rozwiazany':
        return 'bg-green-100 text-green-800';
      case 'zamkniety':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'nowy':
        return 'Nowy';
      case 'w_trakcie':
        return 'W trakcie';
      case 'rozwiazany':
        return 'Rozwiązany';
      case 'zamkniety':
        return 'Zamknięty';
      default:
        return status;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'wysoki':
        return 'bg-red-100 text-red-800';
      case 'sredni':
        return 'bg-yellow-100 text-yellow-800';
      case 'niski':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityLabel = (priority) => {
    switch (priority) {
      case 'wysoki':
        return 'Wysoki';
      case 'sredni':
        return 'Średni';
      case 'niski':
        return 'Niski';
      default:
        return priority;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-700">Błąd podczas ładowania zgłoszeń</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <button
          onClick={() => setShowForm(!showForm)}
          className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
        >
          <Plus className="h-5 w-5" />
          <span>{showForm ? 'Anuluj' : 'Nowe zgłoszenie'}</span>
        </button>
      </div>

      {/* Formularz dodawania */}
      {showForm && (
        <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
          <h2 className="text-xl font-semibold mb-4">Utwórz nowe zgłoszenie</h2>
          <form ref={formRef} onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Temat problemu *
              </label>
              <input
                type="text"
                value={formData.subject}
                onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="Krótki opis problemu"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Opis problemu *
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                rows="6"
                placeholder="Szczegółowy opis problemu..."
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Priorytet
              </label>
              <select
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="niski">Niski</option>
                <option value="sredni">Średni</option>
                <option value="wysoki">Wysoki</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Screenshoty (opcjonalnie)
              </label>
              <div className="text-xs text-gray-500 mb-2 flex items-center space-x-1">
                <ImageIcon className="h-3 w-3" />
                <span>Możesz załączyć pliki lub wkleić screenshot (Ctrl+V)</span>
              </div>
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleFileChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
              {formData.screenshots.length > 0 && (
                <div className="mt-2 space-y-2">
                  {formData.screenshots.map((file, index) => (
                    <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                      <div className="flex items-center space-x-2 flex-1 min-w-0">
                        <ImageIcon className="h-4 w-4 text-gray-500 flex-shrink-0" />
                        <span className="text-sm text-gray-700 truncate">{file.name}</span>
                        <span className="text-xs text-gray-500">
                          ({(file.size / 1024).toFixed(1)} KB)
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeFile(index)}
                        className="text-red-600 hover:text-red-700 flex-shrink-0 ml-2"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex space-x-3">
              <button
                type="submit"
                disabled={createMutation.isLoading}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
              >
                {createMutation.isLoading ? 'Tworzenie...' : 'Utwórz zgłoszenie'}
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Anuluj
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Lista zgłoszeń */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Lista */}
        <div className="lg:col-span-1 space-y-4">
          <h2 className="text-xl font-semibold">Moje zgłoszenia</h2>
          
          {/* Zakładki */}
          <div className="flex flex-wrap gap-2 border-b border-gray-200 pb-2">
            <button
              onClick={() => setActiveTab('wszystkie')}
              className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                activeTab === 'wszystkie'
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Wszystkie ({tickets.length})
            </button>
            <button
              onClick={() => setActiveTab('nowy')}
              className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                activeTab === 'nowy'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Nowy ({tickets.filter(t => t.status === 'nowy').length})
            </button>
            <button
              onClick={() => setActiveTab('w_trakcie')}
              className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                activeTab === 'w_trakcie'
                  ? 'bg-yellow-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              W trakcie ({tickets.filter(t => t.status === 'w_trakcie').length})
            </button>
            <button
              onClick={() => setActiveTab('rozwiazany')}
              className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                activeTab === 'rozwiazany'
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Rozwiązany ({tickets.filter(t => t.status === 'rozwiazany').length})
            </button>
            <button
              onClick={() => setActiveTab('zamkniety')}
              className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                activeTab === 'zamkniety'
                  ? 'bg-gray-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Zamknięty ({tickets.filter(t => t.status === 'zamkniety').length})
            </button>
          </div>

          {filteredTickets.length === 0 ? (
            <div className="bg-gray-50 rounded-lg p-6 text-center">
              <MessageSquare className="h-12 w-12 text-gray-400 mx-auto mb-2" />
              <p className="text-gray-600">Brak zgłoszeń w tej kategorii</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredTickets.map((ticket) => (
                <div
                  key={ticket._id}
                  onClick={() => {
                    setSelectedTicket(ticket);
                    // Nie przełączamy zakładki automatycznie - użytkownik pozostaje w wybranej zakładce
                  }}
                  className={`bg-white rounded-lg shadow-md p-4 border-2 cursor-pointer transition-all ${
                    selectedTicket?._id === ticket._id
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-gray-200 hover:border-primary-300'
                  }`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="font-semibold text-gray-900 flex-1">{ticket.subject}</h3>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(ticket.status)}`}>
                      {getStatusLabel(ticket.status)}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 line-clamp-2 mb-2">{ticket.description}</p>
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <span className={`px-2 py-1 rounded ${getPriorityColor(ticket.priority)}`}>
                      {getPriorityLabel(ticket.priority)}
                    </span>
                    <span>{new Date(ticket.createdAt).toLocaleDateString('pl-PL')}</span>
                  </div>
                  {ticket.screenshots && ticket.screenshots.length > 0 && (
                    <div className="mt-2 flex items-center text-xs text-gray-500">
                      <ImageIcon className="h-3 w-3 mr-1" />
                      <span>{ticket.screenshots.length} screenshot{ticket.screenshots.length !== 1 ? 'y' : ''}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Szczegóły zgłoszenia */}
        <div className="lg:col-span-2">
          {selectedTicket ? (
            <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">{selectedTicket.subject}</h2>
                  <div className="flex items-center space-x-3 mb-4">
                    <span className={`px-3 py-1 rounded text-sm font-medium ${getStatusColor(selectedTicket.status)}`}>
                      {getStatusLabel(selectedTicket.status)}
                    </span>
                    <span className={`px-3 py-1 rounded text-sm font-medium ${getPriorityColor(selectedTicket.priority)}`}>
                      {getPriorityLabel(selectedTicket.priority)}
                    </span>
                  </div>
                </div>
                {(isSuperAdmin || selectedTicket.createdBy._id === user?._id) && (
                  <button
                    onClick={() => handleDelete(selectedTicket._id)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="h-5 w-5" />
                  </button>
                )}
              </div>

              <div className="mb-4">
                <p className="text-gray-700 whitespace-pre-wrap">{selectedTicket.description}</p>
              </div>

              {/* Informacje o zgłoszeniu */}
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Utworzone przez:</span>
                    <p className="font-medium">
                      {selectedTicket.createdBy?.firstName && selectedTicket.createdBy?.lastName
                        ? `${selectedTicket.createdBy.firstName} ${selectedTicket.createdBy.lastName}`
                        : selectedTicket.createdBy?.username || 'Nieznany'}
                    </p>
                  </div>
                  <div>
                    <span className="text-gray-600">Data utworzenia:</span>
                    <p className="font-medium">
                      {new Date(selectedTicket.createdAt).toLocaleString('pl-PL')}
                    </p>
                  </div>
                  {selectedTicket.assignedTo && (
                    <div>
                      <span className="text-gray-600">Przypisane do:</span>
                      <p className="font-medium">
                        {selectedTicket.assignedTo?.firstName && selectedTicket.assignedTo?.lastName
                          ? `${selectedTicket.assignedTo.firstName} ${selectedTicket.assignedTo.lastName}`
                          : selectedTicket.assignedTo?.username || 'Nieznany'}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Screenshoty */}
              {selectedTicket.screenshots && selectedTicket.screenshots.length > 0 && (
                <div className="mb-4">
                  <h3 className="text-lg font-semibold mb-2">Screenshoty</h3>
                  <div className="grid grid-cols-2 gap-4">
                    {selectedTicket.screenshots.map((screenshot, index) => (
                      <div key={index} className="border border-gray-200 rounded-lg overflow-hidden">
                        <img
                          src={`${uploadsUrl}/uploads/${screenshot.path}`}
                          alt={`Screenshot ${index + 1}`}
                          className="w-full h-auto cursor-pointer hover:opacity-90"
                          onClick={() => window.open(`${uploadsUrl}/uploads/${screenshot.path}`, '_blank')}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Zmiana statusu (tylko dla super admina) */}
              {isSuperAdmin && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Zmień status:
                  </label>
                  <select
                    value={selectedTicket.status}
                    onChange={(e) => handleStatusChange(selectedTicket._id, e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="nowy">Nowy</option>
                    <option value="w_trakcie">W trakcie</option>
                    <option value="rozwiazany">Rozwiązany</option>
                    <option value="zamkniety">Zamknięty</option>
                  </select>
                </div>
              )}

              {/* Komentarze */}
              <div className="border-t border-gray-200 pt-4">
                <h3 className="text-lg font-semibold mb-4">Komentarze ({selectedTicket.comments?.length || 0})</h3>
                
                {/* Lista komentarzy */}
                {selectedTicket.comments && selectedTicket.comments.length > 0 && (
                  <div className="space-y-3 mb-4">
                    {selectedTicket.comments.map((comment, index) => (
                      <div key={index} className="bg-gray-50 rounded-lg p-3">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center space-x-2">
                            <User className="h-4 w-4 text-gray-500" />
                            <span className="font-medium text-sm">
                              {comment.createdBy?.firstName && comment.createdBy?.lastName
                                ? `${comment.createdBy.firstName} ${comment.createdBy.lastName}`
                                : comment.createdBy?.username || 'Nieznany'}
                            </span>
                          </div>
                          <span className="text-xs text-gray-500">
                            {new Date(comment.createdAt).toLocaleString('pl-PL')}
                          </span>
                        </div>
                        {comment.text && (
                          <p className="text-gray-700 text-sm whitespace-pre-wrap mb-2">{comment.text}</p>
                        )}
                        {/* Screenshoty w komentarzu */}
                        {comment.screenshots && comment.screenshots.length > 0 && (
                          <div className="grid grid-cols-2 gap-2 mt-2">
                            {comment.screenshots.map((screenshot, screenshotIndex) => (
                              <div key={screenshotIndex} className="border border-gray-200 rounded-lg overflow-hidden">
                                <img
                                  src={`${uploadsUrl}/uploads/${screenshot.path}`}
                                  alt={`Screenshot ${screenshotIndex + 1}`}
                                  className="w-full h-auto cursor-pointer hover:opacity-90"
                                  onClick={() => window.open(`${uploadsUrl}/uploads/${screenshot.path}`, '_blank')}
                                />
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Formularz dodawania komentarza */}
                <form onSubmit={handleAddComment} className="space-y-2">
                  <textarea
                    ref={commentTextareaRef}
                    value={commentText}
                    onChange={(e) => setCommentText(e.target.value)}
                    onPaste={handleCommentPaste}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    rows="3"
                    placeholder="Dodaj komentarz... (możesz wkleić screenshot: Ctrl+V)"
                  />
                  {/* Podgląd screenshotów komentarza */}
                  {commentScreenshots.length > 0 && (
                    <div className="space-y-2">
                      <div className="text-xs text-gray-500 flex items-center space-x-1">
                        <ImageIcon className="h-3 w-3" />
                        <span>Wklejone screenshoty:</span>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        {commentScreenshots.map((file, index) => (
                          <div key={index} className="relative border border-gray-200 rounded-lg overflow-hidden">
                            <img
                              src={URL.createObjectURL(file)}
                              alt={`Preview ${index + 1}`}
                              className="w-full h-auto"
                            />
                            <button
                              type="button"
                              onClick={() => removeCommentScreenshot(index)}
                              className="absolute top-1 right-1 bg-red-600 text-white rounded-full p-1 hover:bg-red-700"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <button
                    type="submit"
                    disabled={(!commentText.trim() && commentScreenshots.length === 0) || addCommentMutation.isLoading}
                    className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
                  >
                    <Send className="h-4 w-4" />
                    <span>{addCommentMutation.isLoading ? 'Wysyłanie...' : 'Dodaj komentarz'}</span>
                  </button>
                </form>
              </div>
            </div>
          ) : (
            <div className="bg-gray-50 rounded-lg p-12 text-center">
              <MessageSquare className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 text-lg">Wybierz zgłoszenie z listy, aby zobaczyć szczegóły</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Tickets;

